import { useCallback } from "react";

import { useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";

/**
 * The API's 429 when an action's security alert could no longer carry its
 * one-tap link to the phone today (apps/api lib/security-alert-sms.ts):
 * `link_alerts_exhausted` (adding a sign-in method) or
 * `two_factor_alerts_exhausted` (setting up two-factor authentication).
 */
type ExhaustedCode = "link_alerts_exhausted" | "two_factor_alerts_exhausted";

function exhausted(err: unknown): { code: ExhaustedCode; hours: number } | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  const body = err.body as { error?: unknown; retryAfter?: unknown } | null;
  const code = body?.error;
  if (code !== "link_alerts_exhausted" && code !== "two_factor_alerts_exhausted") return null;
  const wait =
    typeof body?.retryAfter === "number" && body.retryAfter > 0 ? body.retryAfter : 86_400;
  return { code, hours: Math.max(1, Math.ceil(wait / 3600)) };
}

/** What the refusal means to the person, with the wait in hours; null for any other error. */
export function useAlertsExhaustedMessage(): (err: unknown) => string | null {
  const { t } = useLingui();
  return useCallback(
    (err: unknown) => {
      const refusal = exhausted(err);
      if (!refusal) return null;
      const { hours } = refusal;
      return refusal.code === "link_alerts_exhausted"
        ? t`For your security, no more sign-in methods can be added to your account today. Try again in ${hours} h.`
        : t`For your security, two-factor authentication can't be set up again on your account today. Try again in ${hours} h.`;
    },
    [t],
  );
}
